'use client'

import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { Box, Button, HStack, Spinner, Text, VStack } from '@chakra-ui/react'
import { AppCard } from '@/shared'
import { useMedicalSurveyForm } from '../model/use-medical-survey-form'
import { QuestionInput } from './QuestionInput'
import { RequiredMark } from './RequiredMark'
import { SurveyConfirmation } from './SurveyConfirmation'
import { SurveyStepper } from './SurveyStepper'
import type { SurveyStepItem } from './SurveyStepper'
import { getQuestionCategoryLabel } from '@/entities/questionnaire/lib/get-category-label'
import type { IQuestion } from '@/entities/questionnaire/types'
import {
  useGetDonorCardDetailQuery,
  useGetDonorDetailQuery,
} from '@/entities/donor/api/get-donor.api'

interface Props {
  visitId: string
  donorGender?: 'MALE' | 'FEMALE'
  onCancel?: () => void
  onSuccess?: () => void
}

export function MedicalSurveyForm({
  visitId,
  donorGender,
  onCancel,
  onSuccess,
}: Props) {
  const { t, i18n } = useTranslation()

  const { data: donor } = useGetDonorDetailQuery(visitId)
  const { data: donorCard } = useGetDonorCardDetailQuery(donor?.donor_id ?? '')
  const step = donorCard?.current_status.step ?? null

  const {
    form,
    questions,
    questionsLoading,
    status,
    onSubmit,
    isCompleted,
    isEditing,
    setIsEditing,
    draftKey,
  } = useMedicalSurveyForm(
    visitId,
    donor?.donor_id,
    donor?.gender,
    step,
    onSuccess,
  )

  const {
    control,
    formState: { errors },
  } = form

  const isPending = status === 'pending'
  // Completed surveys render read-only until the user opts into editing.
  const editable = !isCompleted || isEditing

  const groupedCategories = useMemo(() => {
    const map = new Map<string, IQuestion[]>()

    questions
      .filter(
        (q) =>
          !q.targetGender || !donorGender || q.targetGender === donorGender,
      )
      .forEach((q) => {
        const existing = map.get(q.categoryKey)
        map.set(q.categoryKey, [...(existing ?? []), q])
      })

    return [...map.entries()]
      .map(([categoryKey, categoryQuestions]) => ({
        categoryKey,
        label: getQuestionCategoryLabel(categoryQuestions[0], i18n.language),
        questions: [...categoryQuestions].sort(
          (a, b) => a.sortOrder - b.sortOrder,
        ),
      }))
      .sort(
        (a, b) =>
          (a.questions[0]?.sortOrder ?? 0) - (b.questions[0]?.sortOrder ?? 0),
      )
  }, [questions, donorGender, i18n.language])

  // Flattened, category-ordered list driving the one-question-at-a-time flow.
  const stepItems = useMemo<SurveyStepItem[]>(
    () =>
      groupedCategories.flatMap(({ label, questions: categoryQuestions }) =>
        categoryQuestions.map((question) => ({
          question,
          categoryLabel: label,
        })),
      ),
    [groupedCategories],
  )

  if (questionsLoading) {
    return (
      <Box display="flex" justifyContent="center" py={10}>
        <Spinner size="lg" color="primary.fg" />
      </Box>
    )
  }

  // First pass through the survey: ask one question at a time.
  if (!isCompleted) {
    return (
      <form onSubmit={onSubmit}>
        <SurveyStepper
          items={stepItems}
          control={control}
          errors={errors}
          isPending={isPending}
          draftKey={draftKey}
          onCancel={onCancel}
        />
      </form>
    )
  }

  // Already answered — show every question at once for review / editing.
  return (
    <form onSubmit={onSubmit}>
      <VStack gap={5} align="stretch">
        {!isEditing && (
          <Box
            bg="badge.green.20"
            borderWidth="1px"
            borderColor="badge.green.100"
            borderRadius="xl"
            px={4}
            py={3}
          >
            <Text fontSize="sm" fontWeight="medium" color="badge.green.100">
              {t('donor_detail.medical_survey.completed')}
            </Text>
          </Box>
        )}

        {groupedCategories.map(
          ({ categoryKey, label, questions: categoryQuestions }) => (
            <AppCard.Root key={categoryKey}>
              <AppCard.Body p={5}>
                <Text fontWeight="semibold" mb={3}>
                  {label}
                </Text>
                <VStack gap={0} align="stretch" divideY="1px">
                  {categoryQuestions.map((q) => (
                    <Box
                      key={q.questionId}
                      py={3}
                      borderBottomWidth="1px"
                      borderColor="border.muted"
                      _last={{ borderBottomWidth: 0 }}
                    >
                      <Text fontSize="sm" mb={2} lineHeight={1.5}>
                        {q.questionText}
                        {q.required && <RequiredMark />}
                      </Text>
                      <QuestionInput
                        question={q}
                        control={control}
                        disabled={!editable}
                      />
                    </Box>
                  ))}
                </VStack>
              </AppCard.Body>
            </AppCard.Root>
          ),
        )}

        <SurveyConfirmation
          control={control}
          errors={errors}
          disabled={!editable}
        />

        <HStack justify="flex-end" gap={3}>
          {!isEditing ? (
            <Button
              type="button"
              bg="primary.solid"
              color="white"
              borderRadius="md"
              px={8}
              _hover={{ opacity: 0.85 }}
              _active={{ opacity: 0.7 }}
              onClick={() => setIsEditing(true)}
            >
              {t('donor_detail.medical_survey.edit')}
            </Button>
          ) : (
            <>
              <Button
                type="submit"
                bg="primary.solid"
                color="white"
                borderRadius="md"
                px={8}
                _hover={{ opacity: 0.85 }}
                _active={{ opacity: 0.7 }}
                disabled={isPending}
              >
                {isPending ? (
                  <Spinner size="xs" color="white" />
                ) : (
                  t('donor_detail.medical_survey.save')
                )}
              </Button>
              <Button
                type="button"
                variant="outline"
                borderRadius="md"
                color={{ _dark: 'white', _light: 'fg' }}
                px={8}
                onClick={() => setIsEditing(false)}
                disabled={isPending}
              >
                {t('donor_detail.medical_survey.cancel')}
              </Button>
            </>
          )}
        </HStack>
      </VStack>
    </form>
  )
}
